import { useState, useEffect, useCallback } from 'react';
import { Pencil, Check } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Editable text block - admin can click to edit, auto-saves on blur.
 * Language-aware: stores/loads separate content per language.
 */
export const EditableBlock = ({ pageId, sectionId, defaultText, className = '' }) => {
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const isAdmin = user?.is_site_admin === true || user?.user_type === 'admin';
  const langSectionId = `${sectionId}_${language}`;
  const [content, setContent] = useState(defaultText);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(defaultText);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
    axios.get(`${API_URL}/page-content/${pageId}`)
      .then(res => {
        // Try language-specific key first, then fall back to generic
        const saved = res.data[langSectionId] || res.data[sectionId];
        if (saved && language === 'da') {
          // Only use saved content for Danish (original language)
          setContent(saved);
          setValue(saved);
        } else {
          // For other languages, always use the translation
          setContent(defaultText);
          setValue(defaultText);
        }
        setLoaded(true);
      })
      .catch(() => {
        setContent(defaultText);
        setValue(defaultText);
        setLoaded(true);
      });
  }, [pageId, sectionId, langSectionId, language, defaultText]);

  const save = useCallback(async () => {
    setEditing(false);
    if (value !== content) {
      try {
        await axios.put(`${API_URL}/page-content/${pageId}/${langSectionId}`, 
          { content: value }
        );
        setContent(value);
        toast.success(t('common.save'));
      } catch {
        toast.error(t('toasts.genericError'));
        setValue(content);
      }
    }
  }, [value, content, pageId, langSectionId, t]);

  if (!loaded) return <span className={className}>{defaultText}</span>;

  if (!isAdmin) return <span className={className}>{content}</span>;

  if (editing) {
    return (
      <div className="relative">
        <textarea
          value={value}
          onChange={e => setValue(e.target.value)}
          onBlur={save}
          onKeyDown={e => { if (e.key === 'Escape') { setValue(content); setEditing(false); } }}
          autoFocus
          className={`w-full bg-zinc-800 border border-emerald-500/50 rounded-lg px-3 py-2 text-white text-sm resize-y min-h-[60px] focus:outline-none focus:ring-1 focus:ring-emerald-500 ${className}`}
          data-testid={`edit-${pageId}-${sectionId}`}
        />
        <button onClick={save} className="absolute top-1 right-1 p-1 bg-emerald-600 rounded text-white">
          <Check className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      className={`cursor-pointer hover:bg-emerald-500/5 rounded px-0.5 -mx-0.5 transition-colors group relative inline ${className}`}
      data-testid={`cms-${pageId}-${sectionId}`}
    >
      {content}
      <Pencil className="w-3 h-3 text-emerald-400 opacity-0 group-hover:opacity-100 inline ml-1 transition-opacity" />
    </span>
  );
};
